import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, Subject, takeUntil } from 'rxjs';
import { SaleService } from '../../../ticket-purchase/services/sale.service';

@Component({
  selector: 'app-active-sale-indicator',
  standalone: true,
  imports: [CommonModule],
  template: `
    @if (activeSale) {
    <div class="fixed bottom-40 right-4 z-50">
      <div
        class="bg-blue-600 text-white p-4 rounded-lg shadow-lg max-w-sm cursor-pointer hover:bg-blue-700 transition-colors"
        (click)="goToActiveSale()"
      >
        <div class="flex items-center space-x-3">
          <div class="flex-shrink-0">
            <svg
              class="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
              ></path>
            </svg>
          </div>
          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium truncate">Compra en proceso</p>
            <p class="text-xs text-blue-100 truncate">
              {{ activeSale.movie.name }}
            </p>
            <div class="flex items-center mt-1">
              <svg
                class="w-3 h-3 mr-1 text-blue-200"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                ></path>
              </svg>
              <span class="text-xs text-blue-200">{{ timeRemaining }}</span>
            </div>
          </div>
          <div class="flex-shrink-0">
            <svg
              class="w-4 h-4 text-blue-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 5l7 7-7 7"
              ></path>
            </svg>
          </div>
        </div>
      </div>
    </div>
    }
  `,
})
export class ActiveSaleIndicatorComponent implements OnInit, OnDestroy {
  public activeSale: any = null;
  public timeRemaining = '';

  private destroy$ = new Subject<void>();

  constructor(private saleService: SaleService, private router: Router) {}

  ngOnInit(): void {
    // Solo mostrar el indicador si NO estamos en la página de checkout
    this.saleService.saleState$
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        this.updateIndicatorVisibility(state);
      });

    // Escuchar cambios de navegación
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationEnd) => {
        // Si navegamos fuera de checkout y no tenemos venta cargada, verificar en el servidor
        const isLeavingCheckout = !event.url.includes('/checkout');
        const currentSale = this.saleService.currentSale;

        if (isLeavingCheckout && !currentSale) {
          this.checkForActiveSale();
        }

        // Actualizar la visibilidad basada en la nueva URL
        const currentState = {
          currentSale: this.saleService.currentSale,
          loading: this.saleService.loading,
          timeRemaining: this.saleService.timeRemaining,
        };
        this.updateIndicatorVisibility(currentState);
      });

    // Verificación inicial
    if (!this.saleService.currentSale) {
      this.checkForActiveSale();
    }

    // Verificación periódica cada 30 segundos solo si no estamos en checkout
    setInterval(() => {
      const isOnCheckoutPage = this.router.url.includes('/checkout');
      if (!isOnCheckoutPage && !this.saleService.currentSale) {
        this.checkForActiveSale();
      }
    }, 30000);
  }

  private updateIndicatorVisibility(state: any): void {
    const isOnCheckoutPage = this.router.url.includes('/checkout');
    if (state.currentSale?.client && !isOnCheckoutPage) {
      this.activeSale = state.currentSale;
      this.timeRemaining = this.saleService.formatTimeRemaining();
    } else {
      this.activeSale = null;
    }
  }

  private checkForActiveSale(): void {
    this.saleService.refreshSaleState().subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  goToActiveSale(): void {
    if (this.activeSale) {
      this.router.navigate(['/checkout'], {
        queryParams: {
          movieId: this.activeSale.movie.id,
          functionId: this.activeSale.function.id,
        },
      });
    }
  }
}
